'use client';

import React, { FC, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import style from '../Header/header.module.scss';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { Loading } from '@nextui-org/react';

import { ISearchKeywordResponseV2 } from '@/src/v2/service/v2/graphql/generated/gql-api.generated';
import { GetSearchKeyword } from '../../service/v2/graphql/apiv2';
import LogoIcon from '../../assets/icons/LogoIcon';
import { setIsSearchOpen } from '../../store/slices/presentation/PresentationSlice';
import { useAppDispatch } from '../../hooks/useAppDispatch/UseAppDispatch';
import Link from 'next/link';
import {
  getKey,
  getThumbnailKey,
  useThumbnailUrl,
} from '../../hooks/useAvatarUrl/useAvatarUrl';

const Icon = dynamic(() => import('@/src/v2/components/Icon'), { ssr: false });
const HeaderSearch: FC<{ isMenuOpen: boolean }> = ({ isMenuOpen }) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [eventData, setEventData] = useState<ISearchKeywordResponseV2 | null>();
  const [categoryData, setCategoryData] =
    useState<ISearchKeywordResponseV2 | null>();
  const [channelData, setChannelData] =
    useState<ISearchKeywordResponseV2 | null>();
  const [searchValue, setSearchValue] = useState<string>('');
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ImgLogoError, setImgLogoError] = useState<boolean>(false);
  const [imgErrors, setImgErrors] = useState({});
  const pathname = usePathname();
  const isHomePage = pathname === '/';

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    if (searchValue.length > 0) {
      dispatch(setIsSearchOpen(true));
      GetSearchKeyword({ keyword: searchValue.toLowerCase() }).then((data) => {
        if (isMounted) {
          if (data?.getSearchByKeyword && data?.getSearchByKeyword.length > 0) {
            const event = data?.getSearchByKeyword.filter(
              (data) => data.type === 'event'
            );
            const channel = data?.getSearchByKeyword.filter(
              (data) => data.type === 'channel'
            );
            const category = data?.getSearchByKeyword.filter(
              (data) => data.type === 'category'
            );
            setChannelData(channel);
            setEventData(event);
            setCategoryData(category);
          } else {
            setEventData(null);
            setCategoryData(null);
            setChannelData(null);
          }
          setIsLoading(false);
        }
      });
    } else {
      setEventData(null);
      setCategoryData(null);
      setChannelData(null);
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
      dispatch(setIsSearchOpen(false));
    };
  }, [searchValue]);

  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  useEffect(() => {
    if (!isInputFocused) {
      setTimeout(() => {
        setShowSearch(false);
        setSearchValue('');
      }, 500);
    }
  }, [isInputFocused]);
  return (
    <>
      <div
        className={classNames(style['header__search'], {
          [style['header__search--short']]: !isMenuOpen,
        })}
      >
        <div
          className={classNames(style['header__search--home-icon'], {
            [style['header__search--home-icon-focus']]: isInputFocused,
          })}
        >
          {isHomePage ? (
            <Icon icon="homefillIcon" width={20} height={20} />
          ) : (
            <Link href="/">
              <Icon icon="homelineIcon" width={20} height={20} />
            </Link>
          )}
        </div>
        <div className={classNames(style['header__search--input'])}>
          <input
            id={'search'}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={'Search'}
            type={'text'}
            name={'search'}
            autoComplete={'off'}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
          />
          {searchValue.length > 0 && (
            <div
              onClick={() => {
                setSearchValue('');
                setShowSearch(false);
              }}
            >
              <div className={classNames(style['header__search--cancel_icon'])}>
                <Icon icon="menuClose" width={12} height={12} />
              </div>
            </div>
          )}
        </div>
        <div
          className={classNames(style['header__search--search_icon'], {
            [style['header__search--search_icon--focused']]: isInputFocused,
          })}
        >
          <Icon icon="searchWhite" width={16} height={16} />
        </div>
        {isLoading && (
          <div className={classNames(style['header__search--result_data'])}>
            <div
              className={classNames(
                style['header__search--result_data--loading']
              )}
            >
              <Loading size="sm" />
            </div>
          </div>
        )}
        {searchValue.length > 0 && (
          <div className={classNames(style['header__search--result_data'])}>
            {channelData != null && channelData.length > 0 && (
              <div
                className={classNames(
                  style['header__search--result_data--section']
                )}
              >
                {channelData.map((item) => {
                  const hasError = imgErrors[item.title] || false;
                  const imgUrl = getKey('avatar', 'search_bar', item.title);
                  return (
                    <div
                      key={item.title}
                      onClick={() => {
                        document.body.className = '';
                        setSearchValue('');
                        setEventData(null);
                        setShowSearch(false);
                        router.replace(`/${item.title}`);
                      }}
                      className={classNames(
                        style['header__search--sidebar-info-item']
                      )}
                    >
                      <div
                        className={classNames(
                          style[
                            'header__search--sidebar-info-item--profile-content'
                          ]
                        )}
                      >
                        {item.imgUrl && !hasError ? (
                          <div
                            className={
                              style[
                                'header__search--sidebar-info-item--profile-image'
                              ]
                            }
                          >
                            <Image
                              src={`${process.env.NEXT_PUBLIC_ASSET_CDN}${imgUrl}`}
                              alt={item.title}
                              width={40}
                              height={40}
                              loader={(): any => {
                                return `${process.env.NEXT_PUBLIC_ASSET_CDN}${imgUrl}`;
                              }}
                              onError={(e) => {
                                setImgErrors((prev) => ({
                                  ...prev,
                                  [item.title]: true,
                                }));
                              }}
                              onLoadingComplete={(result) => {
                                if (result.naturalWidth === 0) {
                                  setImgErrors((prev) => ({
                                    ...prev,
                                    [item.title]: true,
                                  }));
                                }
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className={
                              style[
                                'header__search--sidebar-info-item--icon_wrapper'
                              ]
                            }
                          >
                            <LogoIcon isThumbnail={true} />
                          </div>
                        )}
                        <div
                          className={
                            style['header__search--sidebar-info-item__content']
                          }
                        >
                          {item.title}
                        </div>
                      </div>
                      {item.isLive === true && (
                        <div
                          className={
                            style['header__search--sidebar-info-item__tags']
                          }
                        >
                          <span>Live</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {eventData != null && eventData.length > 0 && (
              <div
                className={classNames(
                  style['header__search--result_data--section']
                )}
              >
                {eventData.map((item) => {
                  const hasError = imgErrors[item.eventId] || false;
                  const { modifiedUrl: imgUrl } = getThumbnailKey(
                    'eventThumbnail',
                    'thumbnail_search_bar',
                    item.imgUrl,
                    item.userId
                  );
                  return (
                    <div
                      key={item.title}
                      onClick={() => {
                        document.body.className = '';
                        setSearchValue('');
                        setEventData(null);
                        setShowSearch(false);
                        router.replace(
                          `/${item.userId}/event/${item?.eventId}`
                        );
                      }}
                      className={classNames(
                        style['header__search--sidebar-info-item']
                      )}
                    >
                      <div
                        className={classNames(
                          style[
                            'header__search--sidebar-info-item--profile-content'
                          ]
                        )}
                      >
                        {imgUrl && !hasError ? (
                          <div
                            className={`${style['header__search--sidebar-info-item--profile-image']} ${style['header__search--sidebar-info-item--profile-image--event']}`}
                          >
                            <Image
                              src={`${imgUrl}`}
                              alt={item.title}
                              width={40}
                              height={40}
                              style={{ width: 'auto', height: 50 }}
                              loader={(): any => {
                                return `${imgUrl}`;
                              }}
                              onError={(e) => {
                                setImgErrors((prev) => ({
                                  ...prev,
                                  [item.eventId]: true,
                                }));
                              }}
                              onLoadingComplete={(result) => {
                                if (result.naturalWidth === 0) {
                                  setImgErrors((prev) => ({
                                    ...prev,
                                    [item.eventId]: true,
                                  }));
                                }
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className={`${style['header__search--sidebar-info-item--icon_wrapper']} ${style['header__search--sidebar-info-item--icon_wrapper--event']}`}
                          >
                            <LogoIcon isThumbnail={true} width={30} />
                          </div>
                        )}
                        <div
                          className={
                            style['header__search--sidebar-info-item__content']
                          }
                        >
                          {item.title}
                        </div>
                      </div>
                      {item?.eventStatus !== '' && (
                        <div
                          className={
                            style['header__search--sidebar-info-item__tags']
                          }
                        >
                          <span>
                            {item?.eventStatus === 'PAST'
                              ? 'VOD'
                              : item?.eventStatus}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {categoryData != null && categoryData.length > 0 && (
              <div
                className={classNames(
                  style['header__search--result_data--section']
                )}
              >
                {categoryData.map((item) => {
                  const hasError = imgErrors[item.eventId] || false;
                  return (
                    <div
                      key={item.title}
                      onClick={() => {
                        document.body.className = '';
                        setSearchValue('');
                        setEventData(null);
                        setShowSearch(false);
                        if (item.type === 'channel') {
                          router.replace(`/${item.title}`);
                        }
                        if (item.type === 'event') {
                          router.replace(`/categories/${item?.eventId}`);
                        }
                      }}
                      className={classNames(
                        style['header__search--sidebar-info-item']
                      )}
                    >
                      <div
                        className={classNames(
                          style[
                            'header__search--sidebar-info-item--profile-content'
                          ]
                        )}
                      >
                        {item.imgUrl && !hasError ? (
                          <div
                            className={`${style['header__search--sidebar-info-item--profile-image']} ${style['header__search--sidebar-info-item--profile-image--category']}`}
                          >
                            <Image
                              src={`${
                                process.env.NEXT_PUBLIC_ASSET_CDN
                              }${item.imgUrl.replace(
                                /(\.[\w\d_-]+)$/i,
                                '_98x58$1'
                              )}`}
                              alt={item.title}
                              width={40}
                              height={40}
                              style={{ width: 'auto', height: '100%' }}
                              loader={(): any => {
                                return `${
                                  process.env.NEXT_PUBLIC_ASSET_CDN
                                }${item.imgUrl.replace(
                                  /(\.[\w\d_-]+)$/i,
                                  '_98x58$1'
                                )}`;
                              }}
                              onError={(e) => {
                                setImgErrors((prev) => ({
                                  ...prev,
                                  [item.eventId]: true,
                                }));
                              }}
                              onLoadingComplete={(result) => {
                                if (result.naturalWidth === 0) {
                                  setImgErrors((prev) => ({
                                    ...prev,
                                    [item.eventId]: true,
                                  }));
                                }
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className={`${style['header__search--sidebar-info-item--icon_wrapper']} ${style['header__search--sidebar-info-item--icon_wrapper--category']}`}
                          >
                            <LogoIcon isThumbnail={true} />
                          </div>
                        )}
                        <div
                          className={
                            style['header__search--sidebar-info-item__content']
                          }
                        >
                          {item.title}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {(eventData == null || eventData.length === 0) &&
              (channelData == null || channelData.length === 0) &&
              (categoryData == null || categoryData.length === 0) &&
              !isLoading && (
                <div
                  onClick={() => {
                    setSearchValue('');
                    setShowSearch(false);
                  }}
                >
                  <div
                    className={
                      style['header__search--sidebar-info-item__no-found']
                    }
                  >
                    <Icon icon="nofoundIcon" width={84} height={84} />
                    <span>No Result Found!</span>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    </>
  );
};

export default HeaderSearch;
